import React, { useEffect, useMemo, useState } from "react";
import { getDocuments } from "../../api/adminApi";
import { SkeletonTable } from "../common/SkeletonBlock";

function formatDateTime(value) {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

export default function DocumentList({ onOpenDocument }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [revisionFilter, setRevisionFilter] = useState("ALL");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getDocuments();
      setDocs(res.data || []);
    } catch (e) {
      console.error("Failed to load documents", e);
    } finally {
      setLoading(false);
    }
  };

  const revisionOptions = useMemo(() => {
    const values = Array.from(
      new Set((docs || []).map((d) => String(d.revisionNo || 1))),
    );
    return values.sort((a, b) => Number(a) - Number(b));
  }, [docs]);

  const filteredDocs = useMemo(() => {
    return (docs || []).filter((doc) => {
      const matchesSearch =
        !search.trim() ||
        String(doc.name || "")
          .toLowerCase()
          .includes(search.trim().toLowerCase());

      const matchesStatus =
        statusFilter === "ALL" || String(doc.status) === statusFilter;

      const matchesRevision =
        revisionFilter === "ALL" ||
        String(doc.revisionNo || 1) === revisionFilter;

      return matchesSearch && matchesStatus && matchesRevision;
    });
  }, [docs, search, statusFilter, revisionFilter]);

  if (loading) {
    return <SkeletonTable rows={6} cols={5} />;
  }

  return (
    <div className="card">
      <div className="document-filter-panel">
        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div className="form-grid-2 document-filter-grid">
            <div>
              <label className="label">Search by Document Name</label>
              <input
                className="input"
                placeholder="Type document name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div>
              <label className="label">Status Filter</label>
              <select
                className="select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">All Statuses</option>
                <option value="UPLOADED">Uploaded</option>
                <option value="PARTIALLY_SIGNED">Partially Signed</option>
                <option value="SIGNED">Signed</option>
                <option value="REJECTED">Rejected</option>
                <option value="EXPIRED">Expired</option>
              </select>
            </div>

            <div>
              <label className="label">Revision Filter</label>
              <select
                className="select"
                value={revisionFilter}
                onChange={(e) => setRevisionFilter(e.target.value)}
              >
                <option value="ALL">All Revisions</option>
                {revisionOptions.map((rev) => (
                  <option key={rev} value={rev}>
                    Revision {rev}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="document-list-header">
            <div className="document-list-count-pill">
              {filteredDocs.length} Result{filteredDocs.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        {/* <div className="document-filter-actions">
          {search || statusFilter !== "ALL" || revisionFilter !== "ALL" ? (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setSearch("");
                setStatusFilter("ALL");
                setRevisionFilter("ALL");
              }}
            >
              Clear Filters
            </button>
          ) : null}
        </div> */}
      </div>

      {filteredDocs.length === 0 ? (
        <div className="empty-state">
          No documents found for the selected filters.
        </div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Revision</th>
                  <th>Created</th>
                  <th>Open</th>
                </tr>
              </thead>

              <tbody>
                {filteredDocs.map((doc) => (
                  <tr key={doc.id}>
                    <td>
                      <div className="doc-name-cell">
                        <div className="doc-name-main">{doc.name}</div>
                        <div className="doc-name-sub">ID: {doc.id}</div>
                      </div>
                    </td>

                    <td>
                      <span
                        className={`badge badge-${String(doc.status).toLowerCase()}`}
                      >
                        {doc.status}
                      </span>
                    </td>

                    <td>Revision {doc.revisionNo || 1}</td>

                    <td>{formatDateTime(doc.createdAt)}</td>

                    <td>
                      <button
                        className="btn btn-light"
                        onClick={() => onOpenDocument(doc)}
                      >
                        Open Workspace
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
